/**
 * Utilidades para formatear diferentes tipos de datos
 */

/**
 * Formatea un número a formato de moneda
 * @param {number} value - El valor a formatear
 * @param {string} locale - El código de localización (por defecto: 'es-CL')
 * @param {string} currency - El código de moneda (por defecto: 'CLP')
 * @returns {string} - El valor formateado como moneda
 */
export const formatCurrency = (value, locale = 'es-CL', currency = 'CLP') => {
    if (value === null || value === undefined) return '';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  /**
   * Formatea una fecha en el formato deseado
   * @param {string|Date} date - La fecha a formatear
   * @param {string} format - El formato deseado: 'short', 'medium', 'long', 'full' (por defecto: 'medium')
   * @param {string} locale - El código de localización (por defecto: 'es-CL')
   * @returns {string} - La fecha formateada
   */
  export const formatDate = (date, format = 'medium', locale = 'es-CL') => {
    if (!date) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) return '';
    
    const options = {};
    
    switch (format) {
      case 'short':
        options.day = 'numeric';
        options.month = 'numeric';
        options.year = 'numeric';
        break;
      case 'long':
        options.weekday = 'long';
        options.day = 'numeric';
        options.month = 'long';
        options.year = 'numeric';
        break;
      case 'full':
        options.weekday = 'long';
        options.day = 'numeric';
        options.month = 'long';
        options.year = 'numeric';
        options.hour = 'numeric';
        options.minute = 'numeric';
        break;
      case 'time':
        options.hour = 'numeric';
        options.minute = 'numeric';
        break;
      case 'medium':
      default:
        options.day = 'numeric';
        options.month = 'long';
        options.year = 'numeric';
        break;
    }
    
    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  };
  
  /**
   * Formatea un número telefónico con formato chileno
   * @param {string|number} phone - El número a formatear
   * @returns {string} - El número telefónico formateado
   */
  export const formatPhone = (phone) => {
    if (!phone) return '';
    
    const phoneStr = String(phone).replace(/\D/g, '');
    
    if (phoneStr.length === 9) {
      return `+56 ${phoneStr.substring(0, 1)} ${phoneStr.substring(1, 5)} ${phoneStr.substring(5)}`;
    } else if (phoneStr.length === 8) {
      return `+56 9 ${phoneStr.substring(0, 4)} ${phoneStr.substring(4)}`;
    }
    
    return phoneStr;
  };
  
  /**
   * Limita un texto a una cantidad máxima de caracteres
   * @param {string} text - El texto a limitar
   * @param {number} maxLength - Longitud máxima (por defecto: 100)
   * @param {string} suffix - Sufijo a añadir si el texto se acorta (por defecto: '...')
   * @returns {string} - El texto truncado
   */
  export const truncateText = (text, maxLength = 100, suffix = '...') => {
    if (!text) return '';
    
    if (text.length <= maxLength) return text;
    
    return text.substring(0, maxLength).trim() + suffix;
  };
  
  /**
   * Capitaliza la primera letra de cada palabra en un texto
   * @param {string} text - El texto a capitalizar
   * @returns {string} - El texto con la primera letra de cada palabra en mayúscula
   */
  export const capitalizeWords = (text) => {
    if (!text) return '';
    
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.substring(1))
      .join(' ');
  };
  
  /**
   * Formatea un RUT chileno (XX.XXX.XXX-X)
   * @param {string|number} rut - El RUT a formatear
   * @returns {string} - El RUT formateado
   */
  export const formatRut = (rut) => {
    if (!rut) return '';
    
    // Eliminar puntos y guiones
    let value = String(rut).replace(/[^0-9kK]/g, '');
    
    // Obtener dígito verificador
    const dv = value.charAt(value.length - 1);
    
    // Obtener cuerpo del RUT
    const rutBody = value.substring(0, value.length - 1);
    
    // Formatear con puntos
    let formatted = '';
    for (let i = rutBody.length - 1, j = 0; i >= 0; i--, j++) {
      formatted = rutBody.charAt(i) + formatted;
      if ((j + 1) % 3 === 0 && i !== 0) {
        formatted = '.' + formatted;
      }
    }
    
    return `${formatted}-${dv}`;
  };
  
  /**
   * Formatea un número con separador de miles
   * @param {number} value - El número a formatear
   * @param {string} locale - El código de localización (por defecto: 'es-CL')
   * @returns {string} - El número formateado
   */
  export const formatNumber = (value, locale = 'es-CL') => {
    if (value === null || value === undefined) return '';
    
    return new Intl.NumberFormat(locale).format(value);
  };
  
  /**
   * Formatea una patente de vehículo
   * @param {string} patente - La patente a formatear
   * @returns {string} - La patente formateada
   */
  export const formatPatente = (patente) => {
    if (!patente) return '';
    
    // Eliminar espacios y guiones
    const value = String(patente).replace(/[\s-]/g, '').toUpperCase();
    
    // Formato antiguo: LLLL99 (ej. ABCD12)
    if (/^[A-Z]{4}\d{2}$/.test(value)) {
      return `${value.substring(0, 2)}-${value.substring(2, 4)}-${value.substring(4)}`;
    }
    
    // Formato nuevo: LLLL99 (ej. AB-CD-12)
    if (/^[A-Z]{2}\d{2}[A-Z]{2}$/.test(value)) {
      return `${value.substring(0, 2)}-${value.substring(2, 4)}-${value.substring(4)}`;
    }
    
    // Formato para motocicletas: LL999 (ej. AB-123)
    if (/^[A-Z]{2}\d{3}$/.test(value)) {
      return `${value.substring(0, 2)}-${value.substring(2)}`;
    }
    
    return value;
  };